import { useRef, useState, type KeyboardEvent } from 'react';
import { Icon } from '../../ui';
import styles from './FeatureExplorer.module.css';

const features = [
  {
    title: 'Keep spend in sight',
    icon: 'wallet',
    kicker: 'THE BUDGET CHECK',
    headline: 'Know where the month is heading.',
    description:
      'See what you’ve spent against your plan. Spot the gap between today’s pace and where you want to finish.',
    label: 'Monthly ad spend',
    value: '$3,096',
    suffix: 'of $4,000 planned',
    note: '$904 left in your plan',
    detail: 'Spending slightly ahead of plan',
  },
  {
    title: 'Find your stronger ads',
    icon: 'layers',
    kicker: 'THE CAMPAIGN CHECK',
    headline: 'See the performers behind the totals.',
    description:
      'Move from the big picture into campaigns, ad sets and individual ads. Compare spend with results to see what deserves a closer look.',
    label: 'Cost per purchase',
    value: '$18.04',
    suffix: 'across the sample shop',
    note: 'Compare campaigns in one place',
    detail: 'Spend and results, side by side',
  },
  {
    title: 'Catch changes earlier',
    icon: 'bell',
    kicker: 'THE QUICK CHECK-IN',
    headline: 'Less searching. More knowing.',
    description:
      'Set useful alerts for the changes you care about. Find expensive results and celebrate improvements without combing through every campaign.',
    label: 'Campaign watchlist',
    value: 'A heads-up',
    suffix: 'when the numbers shift',
    note: 'You choose what matters',
    detail: 'Alerts with useful context',
  },
] as const;

export function FeatureExplorer() {
  const [selected, setSelected] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const feature = features[selected]!;
  function keyboard(event: KeyboardEvent, index: number) {
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % features.length
        : event.key === 'ArrowLeft'
          ? (index + features.length - 1) % features.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? features.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  }
  return (
    <section className={styles.section} aria-labelledby="explore-title" id="features">
      <div className={styles.heading}>
        <span>YOUR NEXT FIVE-MINUTE CHECK-IN</span>
        <h2 id="explore-title">
          Big-picture clarity.
          <br />
          Right down to the ad.
        </h2>
        <p>A few clearer answers can change your whole day.</p>
      </div>
      <div className={styles.tabs} role="tablist" aria-label="Explore campaign management benefits">
        {features.map((item, index) => (
          <button
            type="button"
            role="tab"
            key={item.title}
            ref={(node) => {
              tabs.current[index] = node;
            }}
            id={`feature-tab-${index}`}
            aria-controls={`feature-panel-${index}`}
            aria-selected={selected === index}
            tabIndex={selected === index ? 0 : -1}
            onClick={() => setSelected(index)}
            onKeyDown={(event) => keyboard(event, index)}
          >
            <Icon name={item.icon} size={20} />
            {item.title}
            <Icon name="arrow-right" size={16} />
          </button>
        ))}
      </div>
      {features.map((_, index) =>
        index === selected ? null : (
          <div
            key={index}
            role="tabpanel"
            id={`feature-panel-${index}`}
            aria-labelledby={`feature-tab-${index}`}
            hidden
          />
        ),
      )}
      <div
        role="tabpanel"
        id={`feature-panel-${selected}`}
        aria-labelledby={`feature-tab-${selected}`}
        tabIndex={0}
        className={styles.panel}
      >
        <div className={styles.copy}>
          <span className={styles.kicker}>{feature.kicker}</span>
          <h3>{feature.headline}</h3>
          <p>{feature.description}</p>
          <span className={styles.detail}>
            <Icon name="shield-check" size={16} />
            Your live Facebook ads stay view-only.
          </span>
        </div>
        <figure className={styles.visual}>
          <figcaption>
            <span>Juniper Home Goods</span>
            <span>Illustrative sample</span>
          </figcaption>
          <div className={styles.metric}>
            <span>{feature.label}</span>
            <strong>{feature.value}</strong>
            <span>{feature.suffix}</span>
          </div>
          {selected === 0 ? (
            <>
              <div className={styles.budgetTrack}>
                <span />
              </div>
              <div className={styles.budgetLegend}>
                <span>Month spent · 77%</span>
                <strong>{feature.note}</strong>
              </div>
            </>
          ) : selected === 1 ? (
            <div className={styles.rows}>
              {[
                ['Summer favorites', '$14.20', 58],
                ['Everyday essentials', '$18.60', 77],
                ['Weekend collection', '$24.90', 100],
              ].map(([name, amount, width]) => (
                <div key={name}>
                  <span>{name}</span>
                  <div>
                    <span style={{ width: `${width}%` }} />
                  </div>
                  <strong>{amount}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.alerts}>
              <div>
                <Icon name="bell" size={20} />
                <span>
                  <strong>Worth a closer look</strong>Cost per purchase is above your alert
                  threshold.
                </span>
              </div>
              <div>
                <Icon name="check" size={20} />
                <span>
                  <strong>A good day for results</strong>Your best purchase day in the last 30 days.
                </span>
              </div>
            </div>
          )}
          <div className={styles.visualFoot}>
            <Icon name="check" size={16} />
            {feature.detail}
          </div>
        </figure>
      </div>
    </section>
  );
}
